import React, { useCallback } from 'react';
import {
  CommercialOfferStatus,
  getStatusTagColor as getStatusColor,
  isRejected as checkIsRejected,
  statusDisplayNames,
} from '../utils/statusActions';
import { CommercialOfferModel } from 'models/CommercialOffer';
import { convertEnumsToReadableStrings } from 'utils/helpers';

interface UseCommercialOfferCardProps {
  commercialOffer: CommercialOfferModel;
  onView?: (id: string) => void;
  onAction?: (action: string, id: string, uuid: string) => void;
  onCreateCopy?: (id: string) => void;
  onDelete: (id: string) => void;
}

const FINAL_STATUSES = ['PRE_INVOICE_SENT', 'ORDER_CONFIRMATION_SENT'];
export const useCommercialOfferCard = ({
  commercialOffer,
  onView,
  onAction,
  onCreateCopy,
  onDelete,
}: UseCommercialOfferCardProps) => {
  const { id, status, negotiatePhase, uuid } = commercialOffer;

  const handleView = useCallback(() => {
    if (onView) {
      onView(id);
    }
  }, [id, onView]);

  const handleAction = useCallback(
    (action: string) => {
      if (onAction) {
        onAction(action, id, uuid);
      }
    },
    [id, onAction, uuid]
  );

  const handleCreateCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onCreateCopy) {
        onCreateCopy(id);
      }
    },
    [id, onCreateCopy]
  );

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  const getStatusTagColor = useCallback(() => {
    return getStatusColor(status);
  }, [status]);

  const getStatusDisplayText = useCallback(() => {
    const normalizedStatus = status.toUpperCase();

    // Check if it's a negotiation with a phase
    if (normalizedStatus === CommercialOfferStatus.NEGOTIATION) {
      return `Negotiate: ${convertEnumsToReadableStrings(negotiatePhase || '')}`;
    }

    // If the status is in our mapping, use that
    if (normalizedStatus in statusDisplayNames) {
      return statusDisplayNames[normalizedStatus];
    }

    return status;
  }, [status, negotiatePhase]);

  const isRejected = checkIsRejected(status);

  const displayDate = useCallback((date: string) => {
    return date ? new Date(date).toLocaleDateString() : '';
  }, []);

  return {
    handleView,
    handleAction,
    handleCreateCopy,
    handleDelete,
    getStatusTagColor,
    getStatusDisplayText,
    displayDate,
    isRejected,
    isInFinalStatus: FINAL_STATUSES.includes(status),
  };
};
